import AwesomeDebouncePromise from 'awesome-debounce-promise';
import autobind from 'autobind';
import { v4 as uuid } from 'uuid';

import {
    MedicalCaseCreateParams,
    MedicalCaseHistoryQuery,
    MedicalCaseUpdateParams,
    OutwardReferralCreationParams,
    OutwardReferralData,
    OutwardReferralExecutionChangingParams,
    OutwardReferralUpdatingParams,
    RecommendationCreationParams,
    RecommendationData,
    RecommendationUpdateParams,
    ReferralCreateData,
} from 'chaika-frontend-types/frontend-cms';
import { DrugStatus, EntryTypeFieldType, ReferralStatus, MedicalCaseStatus } from 'chaika-enums';

import { DrugApi } from '@api/DrugApi';
import { MedicalCaseApi } from '@api/MedicalCaseApi';
import { OutwardReferralApi } from '@api/OutwardReferralApi';
import { RecommendationApi } from '@api/RecommendationApi';
import { ehrService } from '@patient/services/ehr/ehrService';

import { store } from '@store';

import {
    IMedicalCaseService,
    MedicalCaseRecommendationIdentity,
    MedicalCaseService as CommonMedicalCaseService,
} from '@store/services/MedicalCaseService';

import { DrugParams } from '@components/Blocks';
import { referralService } from '@patient/services/ehr/referralService';
import { ReferralList } from '@store/models/SystemFields/Referral';
import { MedicalCaseList as MedicalCaseStore, MedicalCaseList } from '@store/models/SystemFields/MedicalCase';

import { entryListService } from './entryListService';
import { isDefined } from '@utils/isDefined';
import { translate } from '@chaika/i18n/helper';
import { PopupModifiers, PopupTheme } from '@components/Popup';
import { queryClient } from '@store/queryClient';
import { userQueryOptions } from '@user/queries/userQueries';
import { getShortFullName } from '@user/utils/getShortFullName';

export enum MedicalCaseRelation {
    Drugs = 'drugs',
    Recommendations = 'recommendations',
    // Versions = 'versions',
    Entries = 'entries',
    OutwardReferral = 'outwardReferral',
}

type LoadRelationFunction = (medicalCaseId: string) => void;

class MedicalCaseService extends CommonMedicalCaseService implements IMedicalCaseService {
    private updateDrugApi = AwesomeDebouncePromise(DrugApi.update, 500);

    private relationLoader: Record<MedicalCaseRelation, LoadRelationFunction> = {
        [MedicalCaseRelation.Drugs]: this.loadDrugs,
        [MedicalCaseRelation.Recommendations]: this.loadRecommendations,
        // Если грузить версии то они перебивают ранее загруженную по айдишнику сузщность
        // [MedicalCaseRelation.Versions]: this.loadVersions,
        [MedicalCaseRelation.Entries]: this.loadEntries,
        [MedicalCaseRelation.OutwardReferral]: this.loadOutwardReferral,
    };

    public async load(): Promise<void> {
        const ehr = store.ehr;

        if (!!ehr.id) {
            const medicalCases = await MedicalCaseApi.find({ ehrId: ehr.id });
            await super.loadMedicalCaseData(medicalCases);

            ehr.medicalCases.set(medicalCases);
        } else {
            console.error('TRY LOAD WITHOUT EHR');
        }
    }

    public async loadByIds(
        medicalCaseList: MedicalCaseList,
        ids: string[],
        relations?: MedicalCaseRelation[],
    ): Promise<void> {
        const medicalCases = await MedicalCaseApi.getByIds(ids);

        medicalCaseList.add(medicalCases);
    }

    @autobind
    public async loadWithVersions({
        to,
        ehrId = store.ehr.id,
        id,
    }: Partial<MedicalCaseHistoryQuery> = {}): Promise<void> {
        const medicalCasesWithVersions = await MedicalCaseApi.getMedicalCaseHistory({
            to,
            ehrId,
            id,
        });

        await this.loadMedicalCaseData(medicalCasesWithVersions);

        store.ehr.medicalCases.setWithVersions(medicalCasesWithVersions);
    }

    public async loadById(medicalCaseId: string, relations?: MedicalCaseRelation[]): Promise<void> {
        const ehr = store.ehr;

        if (!ehr.medicalCases.has(medicalCaseId)) {
            const medicalCase = await MedicalCaseApi.find({ ehrId: ehr.id, id: medicalCaseId });
            await this.loadMedicalCaseData(medicalCase);

            ehr.medicalCases.add(medicalCase);
        }

        if (relations) {
            relations.forEach(relation => this.relationLoader[relation](medicalCaseId));
        }
    }

    public async create(params: MedicalCaseCreateParams): Promise<void> {
        const ehr = store.ehr;

        await this.createMedicalCase(ehr.medicalCases, params);
    }

    public async update(
        id: string,
        params: MedicalCaseUpdateParams,
        medicalCaseList?: MedicalCaseStore,
        entryId?: string,
    ): Promise<void> {
        const { ehr } = store;

        if (!this.checkIfMedicalCaseIsClosingAndShowPopup(id, medicalCaseList)) {
            await this.updateMedicalCase(medicalCaseList ?? ehr.medicalCases, id, params, entryId);
        }
    }

    public checkIfMedicalCaseIsClosingAndShowPopup(id: string, medicalCaseList?: MedicalCaseStore): boolean {
        const { authUser, ehr } = store;

        const medicalCases = medicalCaseList ?? ehr.medicalCases;

        const { closeData } = medicalCases.get(id) ?? {};

        if (isDefined(closeData) && closeData.doctorId !== authUser.id) {
            void this.showClosingByAnotherDoctorPopup(closeData.doctorId);

            return true;
        }

        return false;
    }

    public async updateShareLink(id: string, welcomeMessage: string): Promise<void> {
        await this.updateMedicalCaseShareLink(store.ehr.medicalCases, id, welcomeMessage);
    }

    public async updateStatus(
        id: string,
        status: MedicalCaseStatus,
        medicalCaseList?: MedicalCaseStore,
    ): Promise<void> {
        const ehr = store.ehr;

        await this.updateMedicalCaseStatus(medicalCaseList ?? ehr.medicalCases, id, status);
    }

    public async closeMedicalCase(
        medicalCaseId: string,
        entryId: string,
        medicalCases?: MedicalCaseList,
    ): Promise<void> {
        const ehr = store.ehr;

        await this.removeActiveItemsFromMedicalCase(medicalCaseId);
        await this.updateMedicalCaseStatus(
            medicalCases ?? ehr.medicalCases,
            medicalCaseId,
            MedicalCaseStatus.Closed,
            entryId,
        );
    }

    public async removeActiveItemsFromMedicalCase(medicalCaseId: string): Promise<void> {
        const { ehr } = store;
        const { referralIds } = ehr.medicalCases.getExisting(medicalCaseId);

        const activeReferrals = referralService
            .getReferralsByIds(referralIds)
            .filter(referral => referral.status === ReferralStatus.Active);

        await Promise.all([...activeReferrals.map(({ groupId }) => referralService.remove(groupId))]);
    }

    public async remove(id: string): Promise<void> {
        const ehr = store.ehr;

        await this.removeMedicalCase(ehr.medicalCases, id);
    }

    public async createRecommendation(
        medicalCaseId: string,
        recommendationParams: Omit<RecommendationCreationParams, 'medicalCaseIds'>,
    ): Promise<RecommendationData> {
        const recommendation: RecommendationCreationParams = {
            ...recommendationParams,
            medicalCaseIds: [medicalCaseId],
        };

        await RecommendationApi.create(medicalCaseId, recommendation);

        const created = {
            medicalCaseId,
            createdAt: Date.now().toString(),
            authorId: store.authUser.id,
            isImportant: false,
            ...recommendation,
        } as RecommendationData;
        store.ehr.medicalCases.addRecommendation(medicalCaseId, created);

        return created;
    }

    public async updateRecommendation(
        identity: MedicalCaseRecommendationIdentity,
        params: RecommendationUpdateParams,
    ): Promise<void> {
        await this.updateMedicalCaseRecommendation(store.ehr.medicalCases, identity, params);
    }

    public async removeRecommendation(identity: MedicalCaseRecommendationIdentity): Promise<void> {
        await this.removeMedicalCaseRecommendation(store.ehr.medicalCases, identity);
    }

    public async changeRecommendationImportance(
        identity: MedicalCaseRecommendationIdentity,
        isImportant: boolean,
    ): Promise<void> {
        await this.updateMedicalCaseRecommendationImportance(store.ehr.medicalCases, identity, isImportant);
    }

    public async loadOutwardReferrals(medicalCaseIds: string[]): Promise<void> {
        await this.loadMedicalCaseOutwardReferral(store.ehr.medicalCases, medicalCaseIds);
    }

    public async loadOutwardReferralForMedicalCases(medicalCaseIds: string[]): Promise<void> {
        await this.loadOutwardReferrals(medicalCaseIds);
    }

    public async createOutwardReferral(
        medicalCaseId: string,
        params: OutwardReferralCreationParams,
    ): Promise<OutwardReferralData> {
        return this.createMedicalCaseOutwardReferral(store.ehr.medicalCases, medicalCaseId, params);
    }

    public async updateOutwardReferral(
        outwardReferralId: string,
        params: OutwardReferralUpdatingParams,
    ): Promise<void> {
        await this.updateMedicalCaseOutwardReferral(store.ehr.medicalCases, outwardReferralId, params);
    }

    public async updateOutwardReferralStatus(
        outwardReferralId: string,
        params: OutwardReferralExecutionChangingParams,
    ): Promise<void> {
        await this.updateMedicalCaseOutwardReferralStatus(store.ehr.medicalCases, outwardReferralId, {
            ...params,
            executionEntryId: undefined,
        });
    }

    public async removeOutwardReferral(outwardReferralId: string): Promise<void> {
        await this.removeMedicalCaseOutwardReferral(store.ehr.medicalCases, outwardReferralId);
    }

    public async createReferral(ids: string[], referral: ReferralCreateData): Promise<void> {
        const withDefaultOrganization = await this.setOrganizationIdToCreateReferralParams(referral);
        const referrals = ids.map(id => ({
            ...withDefaultOrganization,
            id,
        }));
        await referralService.create(referrals);
        this.addReferrals(store.ehr.medicalCases, referrals);
    }

    public async createReferrals(referrals: ReferralCreateData[]): Promise<void> {
        const createParams = await Promise.all(
            referrals.map(referral => this.setOrganizationIdToCreateReferralParams(referral)),
        );
        await referralService.create(createParams, true);
        await ehrService.reloadReferrals();
        this.addReferrals(store.ehr.medicalCases, createParams);
    }

    public async attachToEntry(medicalCaseId: string): Promise<void> {
        return undefined;
    }

    public async attachReferral(
        medicalCaseId: string,
        referralGroupId: string,
        referringDoctorId: string,
    ): Promise<void> {
        const fields = store.ehr.fields;
        const referralList = fields.getValue<ReferralList>(EntryTypeFieldType.Referrals);

        const referral = referralList.getExisting(referralGroupId);

        await referralService.attachReferral(medicalCaseId, referralGroupId, referringDoctorId);
        this.addReferral(store.ehr.medicalCases, {
            ...referral,
            medicalCaseIds: [medicalCaseId],
        });
    }

    public async dettachReferral(
        medicalCaseId: string,
        referralGroupId: string,
        referringDoctorId: string,
    ): Promise<void> {
        const fields = store.ehr.fields;
        const referralList = fields.getValue<ReferralList>(EntryTypeFieldType.Referrals);

        const referral = referralList.getExisting(referralGroupId);

        await referralService.dettach(medicalCaseId, referralGroupId, referringDoctorId);
        referralList.remove(referralGroupId);
        this.removeReferral(store.ehr.medicalCases, {
            ...referral,
            medicalCaseIds: [medicalCaseId],
        });
    }

    public async loadDrugs(): Promise<void> {
        const ehr = store.ehr;

        const drugs = await DrugApi.getList({ ehrId: ehr.id });

        store.entry.drugList.set(drugs);
    }

    public async createDrug(ids: string[], medicalCaseId: string, createParams: DrugParams): Promise<void> {
        const ehr = store.ehr;

        const medicalCase = ehr.medicalCases.getExisting(medicalCaseId);

        const id = uuid();

        if (medicalCase.parentEntryId) {
            await DrugApi.create({
                id,
                entryId: medicalCase.parentEntryId,
                medicalCaseIds: [medicalCaseId],
                ...createParams,
            });

            const drugParams = {
                id,
                authorId: store.authUser.id,
                createdAt: Date.now().toString(),
                ehrId: ehr.id,
                status: DrugStatus.Active,
                parentEntryId: medicalCase.parentEntryId,
                ...createParams,
            };

            store.entry.drugList.add([drugParams]);
            store.entry.drugHistoryList.add([
                {
                    ...drugParams,
                    ...createParams,
                    metaData: [{ entryId: medicalCase.parentEntryId }],
                },
            ]);
            medicalCase.addDrugIds([id]);
        }
    }

    public async updateDrug(drugId: string, drug: DrugParams, parentEntryId: string): Promise<void> {
        await this.updateDrugApi(drugId, { entryId: parentEntryId, ...drug });
        store.entry.drugList.update(drugId, drug);

        const drugHistory = store.entry.drugHistoryList.getExisting(drugId);
        store.entry.drugHistoryList.update(drugId, {
            ...drug,
            metaData: [...drugHistory.metaData, { entryId: parentEntryId }],
        });
    }

    private async showClosingByAnotherDoctorPopup(doctorId: string): Promise<void> {
        const { app } = store;

        const [doctor] = await queryClient
            .fetchQuery(userQueryOptions.list({ ids: [doctorId] }))
            .catch(() => [] as const);

        app.showPopup({
            description: translate('anotherDoctorWantsToCloseMedicalCase', {
                $doctorName: doctor ? getShortFullName(doctor) : '-',
            }),
            theme: PopupTheme.Primary,
            modifiers: [PopupModifiers.Redesign],
            onClose: () => app.showPopup(null),
        });
    }

    private async loadRecommendations(medicalCaseId: string): Promise<void> {
        const recommendations = await RecommendationApi.findByMedicalCaseIds([medicalCaseId]);

        store.ehr.medicalCases.setRecommendations(medicalCaseId, recommendations);
    }

    @autobind
    private async loadOutwardReferral(medicalCaseId: string): Promise<void> {
        const outwardReferrals = await OutwardReferralApi.find({ medicalCaseId });
        store.ehr.medicalCases.setOutwardReferrals(medicalCaseId, outwardReferrals);
    }

    @autobind
    private async loadEntries(medicalCaseId: string): Promise<void> {
        const { entryList, medicalCases } = store.ehr;
        const medicalCase = medicalCases.get(medicalCaseId);

        const ids = medicalCase?.relatedEntriesIds ?? [];

        if (ids.length > 0) {
            await entryListService.loadEntryList(entryList, { ids: ids.join(',') });
        }
    }
}

export const medicalCaseService = new MedicalCaseService();
